import { useState, type FormEvent } from "react";
import { categories, navLinks, siteName, siteSlogan } from "@/mocks/guaData";

const SUBMIT_URL = "https://readdy.ai/api/form/daf8onjep053ijub1la0";

type Status = "idle" | "sending" | "success" | "error";

export default function Footer() {
  const [status, setStatus] = useState<Status>("idle");
  const [msg, setMsg] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honey = (fd.get("website_alt") || "").toString().trim();

    // 蜜罐命中：静默按“已提交成功”处理，不把信号暴露给机器人
    if (honey) {
      setStatus("success");
      setMsg("订阅成功，感谢关注瓜讯！");
      form.reset();
      return;
    }

    const email = (fd.get("email") || "").toString().trim();
    if (!email) {
      setStatus("error");
      setMsg("请先填写你的邮箱地址。");
      return;
    }

    fd.delete("website_alt");
    setStatus("sending");
    try {
      const params = new URLSearchParams();
      fd.forEach((value, key) => params.append(key, value.toString()));
      const resp = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const text = await resp.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || text;
      const isSpam = typeof serverMsg === "string" && serverMsg.includes("spam");
      const ok = resp.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setMsg("订阅成功，新鲜瓜讯将第一时间送达你的邮箱。");
        form.reset();
      } else {
        setStatus("error");
        setMsg(isSpam ? "提交过于频繁，请稍后再试。" : serverMsg || "提交失败，请检查网络后重试。");
      }
    } catch {
      setStatus("error");
      setMsg("网络似乎开了小差，请稍后重试。");
    }
  };

  return (
    <footer id="subscribe" className="scroll-mt-24 bg-primary-900 text-background-50">
      <div className="mx-auto max-w-7xl px-4 pt-14 pb-8 md:px-6 md:pt-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <a href="#top" className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-500 text-base font-bold text-foreground-950">
                瓜
              </span>
              <span className="text-lg font-bold tracking-wide">{siteName}</span>
            </a>
            <p className="mt-4 max-w-sm text-sm leading-6 text-primary-100">{siteSlogan}，聚合全网最热事件，理性吃瓜不迷路。</p>
            <div className="mt-5 flex items-center gap-3">
              <span className="text-xs text-primary-200">关注我们</span>
              {["ri-weibo-line", "ri-wechat-2-line", "ri-bilibili-line", "ri-rss-line"].map((ic) => (
                <span
                  key={ic}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-800 text-primary-100 transition-colors hover:bg-accent-500 hover:text-foreground-950"
                  role="img"
                  aria-label="社交媒体入口"
                >
                  <i className={`${ic} text-[18px]`}></i>
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-sm font-bold tracking-wide text-accent-300">热门栏目</h3>
            <ul className="mt-4 flex flex-col gap-2.5">
              {categories.map((c) => (
                <li key={c.id}>
                  <a href="#feed" className="text-sm text-primary-100 transition-colors hover:text-background-50">
                    {c.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-sm font-bold tracking-wide text-accent-300">快速导航</h3>
            <ul className="mt-4 flex flex-col gap-2.5">
              {navLinks.map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="text-sm text-primary-100 transition-colors hover:text-background-50">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h3 className="text-sm font-bold tracking-wide text-accent-300">订阅瓜讯</h3>
            <p className="mt-3 text-sm leading-6 text-primary-100">
              留下邮箱，我们会在每日榜单出炉后，把当天最值得聊的几件事打包发给你。
            </p>
            <form
              id="subscribe-form"
              data-readdy-form="订阅瓜讯"
              onSubmit={handleSubmit}
              noValidate={false}
              className="mt-4"
            >
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="输入你的邮箱"
                  aria-label="订阅邮箱"
                  className="w-full rounded-md border border-primary-700 bg-primary-950/40 px-4 py-2.5 text-sm text-background-50 outline-none placeholder:text-primary-300 focus:border-accent-400 focus:ring-2 focus:ring-accent-400/30"
                />
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === "sending" ? (
                    <>
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-loader-4-line animate-spin text-[15px]"></i>
                      </span>
                      提交中
                    </>
                  ) : (
                    "立即订阅"
                  )}
                </button>
              </div>
              {/* 防垃圾邮件蜜罐字段（视觉隐藏） */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />
              <div aria-live="polite" className="mt-3 min-h-5 text-sm">
                {status === "success" && <p className="text-accent-200">✓ {msg}</p>}
                {status === "error" && <p className="text-background-100">{msg}</p>}
              </div>
            </form>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-primary-800 pt-6 text-xs text-primary-200 sm:flex-row sm:items-center sm:justify-between">
          <p>© 2026 {siteName} · 全网娱乐热点排行站。本站仅做公开信息聚合与热度展示，倡导理性吃瓜、不信谣不传谣。</p>
          <a href="mailto:hi@guachangbang.example" className="whitespace-nowrap transition-colors hover:text-accent-300">
            hi@guachangbang.example
          </a>
        </div>
      </div>
    </footer>
  );
}